import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  SiteArea,
  SiteAreaFilterInputObject,
  SiteAreaUpdateInputObject,
  useSiteAreaQuery,
  useSiteAreaUpdateMutation,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import Table from '@lib/components/Table/Table';
import Search from '@lib/components/Search/Search';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Trans from '@lib/components/Trans/Trans';
import toast from '@lib/components/Toast/Toast';
import { getErrorMessage } from '@lib/utils/graphQLErrors';
import { Values } from '@lib/interfaces/form';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { PlanState } from '@lib/enums/plan';
import { TableAction } from '@lib/components/Table/types';
import useSiteAreasQueryHook from 'utils/fetch/hooks/useSiteAreasQueryHook';
import SiteAreaViewEditModal from 'components/SiteArea/SiteAreaViewEditModal/SiteAreaViewEditModal';
import getSiteAreaPreparedDataFromValues from 'routes/Sites/utils/getSiteAreaPreparedDataFromValues';
import useTabListState from '@lib/components/Table/utils/useTabListState';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import getAreasColumns from './utils/getAreasColumns';

const SEARCH_FILTER_SCHEMA = [
  'name',
  'code',
  'siteName',
  'siteCode',
  'sectorName',
];

interface AreasTabProps {
  documentId: string;
}

export default function AreasTab({ documentId }: AreasTabProps) {
  const { t } = useTranslation();
  const { listState, params, searchQuery, onSearchChange } = useTabListState();
  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    handleOpenPlanModal,
  }: ContextProps = useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  const isDocumentationLocked =
    isPlanLocked ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;

  const [selectedArea, setSelectedArea] = useState<SiteArea | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const additionalFilters: SiteAreaFilterInputObject[] = useMemo(
    () => [
      {
        documentationsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: documentId },
          filterGroup: 'documentationsId',
        },
      },
    ],
    [documentId],
  );

  const siteAreasQueryHookResult = useSiteAreasQueryHook({
    searchQuery,
    paginationParams: params,
    additionalFilters,
    includeSiteData: true,
    includeFullData: true,
    searchQueryFilterSchema: SEARCH_FILTER_SCHEMA,
    skip: isPlanLocked,
  });

  const areaId = selectedArea?.id || '';
  const siteId = _get(selectedArea, 'site.id', '') as string;
  const siteAreaQuery = useSiteAreaQuery({
    skip: !areaId || !siteId,
    variables: { findBy: { id: areaId }, siteFindBy: { id: siteId } },
  });
  const siteAreaData = _get(
    siteAreaQuery,
    'data.data',
    selectedArea,
  ) as SiteArea;

  const [updateMutation, { loading: updateLoading, error: updateError }] =
    useSiteAreaUpdateMutation({
      onCompleted: () => {
        toast({ content: t('update-site-area-success') });
        setIsViewModalOpen(false);
        setIsEditMode(false);
        setSelectedArea(null);
        siteAreasQueryHookResult.refetch();
      },
      onError: (error) => {
        toast({ content: getErrorMessage(error, t) });
      },
    });

  const onViewHandler = useCallback((item?: SiteArea) => {
    if (item?.id) {
      setSelectedArea(item);
      setIsEditMode(false);
      setIsViewModalOpen(true);
    }
  }, []);

  const onCloseModal = useCallback(() => {
    setIsViewModalOpen(false);
    setIsEditMode(false);
    setSelectedArea(null);
  }, []);

  const onToggleMode = useCallback(() => {
    setIsEditMode((prev) => !prev);
  }, []);

  const onUpdateHandler = useCallback(
    (values: Values) => {
      if (values?.id) {
        updateMutation({
          variables: {
            id: values.id as string,
            attributes: getSiteAreaPreparedDataFromValues(
              values,
            ) as SiteAreaUpdateInputObject,
          },
        });
      }
    },
    [updateMutation],
  );

  const columns = useMemo(
    () => getAreasColumns({ onViewHandler, t }),
    [onViewHandler, t],
  );

  const tableActions: TableAction<SiteArea>[] = useMemo(
    () => [
      {
        id: 'view-action',
        icon: 'visibility',
        onClick: (item: SiteArea) => onViewHandler(item),
        type: IconButtonTypes.PrimaryOutlined,
        predictorFnTooltipProps: () => ({ body: t('view') }),
      },
    ],
    [onViewHandler, t],
  );

  if (isPlanLocked) {
    return (
      <FeatureLockState
        title={t('unlock-area-level-documentation')}
        text={<Trans i18nKey="unlock-area-level-documentation-text" />}
        onUpgrade={() =>
          handleOpenPlanModal(PlanState.Locked, [
            CompanyPlanUpgradeFeatureEnum.Documentation,
          ])
        }
      />
    );
  }

  if (siteAreasQueryHookResult.firstLoading) {
    return <CircleLoader />;
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-linked-areas')}
      text={t('no-linked-areas-text')}
    />
  );

  return (
    <>
      {((!!siteAreasQueryHookResult.totalCount &&
        !!siteAreasQueryHookResult.collection.length) ||
        searchQuery) && (
        <Search
          placeholder={t('document-areas-search-placeholder')}
          value={searchQuery}
          onChange={onSearchChange}
          useSearchParam={false}
        />
      )}
      <Table
        actions={tableActions}
        columns={columns}
        counterText={t('area-plural', {
          count: siteAreasQueryHookResult.totalCount,
        })}
        data={siteAreasQueryHookResult.collection}
        emptyState={emptyState}
        id="documentAreas"
        loading={siteAreasQueryHookResult.loading}
        orderingState={listState}
        paginationState={listState}
        totalEntries={siteAreasQueryHookResult.totalCount}
      />
      <SiteAreaViewEditModal
        areaData={siteAreaData}
        graphQLErrors={updateError?.graphQLErrors}
        isDocumentationLocked={isDocumentationLocked}
        isEditMode={isEditMode}
        isOpen={isViewModalOpen}
        loading={updateLoading}
        onClose={onCloseModal}
        onSubmit={onUpdateHandler}
        onToggleMode={onToggleMode}
      />
    </>
  );
}
